'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';

export type SupportQueryType = 'INTERNAL_ISSUE' | 'TECHNICAL_SUPPORT';
export type SupportTicketStatus =
  | 'NEW'
  | 'IN_PROGRESS'
  | 'WAITING_FOR_USER'
  | 'WAITING_INTERNAL'
  | 'RESOLVED'
  | 'CLOSED';
export type SupportPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type SupportControlOwner = 'HELP_DESK' | 'DEVELOPER_SUPPORT';

export interface SupportAttachment {
  id: string;
  fileId: string;
  name: string;
  size: number;
  mimeType: string;
  createdAt: string;
}

export interface SupportTicket {
  id: string;
  ticketCode: string;
  tenantId: string | null;
  queryType: SupportQueryType;
  subject: string;
  status: SupportTicketStatus;
  priority: SupportPriority | null;
  category: string | null;
  createdByUserId: string | null;
  requesterName: string | null;
  requesterEmail: string | null;
  controlOwner: SupportControlOwner;
  assigneeUserId: string | null;
  contractId: string | null;
  projectSiteId: string | null;
  typePayload: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface SupportMessage {
  id: string;
  ticketId: string;
  senderUserId: string | null;
  senderName: string | null;
  senderRole: string | null;
  body: string;
  isInternal: boolean;
  createdAt: string;
  attachments?: SupportAttachment[];
}

export interface TenantTicketDetail extends SupportTicket {
  messages: SupportMessage[];
}

export interface CreatePublicTicketInput {
  fullName: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  category?: string;
}

export interface CreatePublicTicketResponse {
  ticketCode: string;
  status: SupportTicketStatus;
  createdAt: string;
  message: string;
}

export interface PublicTicketDetail {
  ticketCode: string;
  subject: string;
  status: SupportTicketStatus;
  category: string | null;
  createdAt: string;
  updatedAt: string;
  messages: SupportMessage[];
}

export interface CreateTicketInput {
  queryType?: SupportQueryType;
  subject: string;
  description: string;
  category: string;
  priority?: SupportPriority;
  contractId?: string | null;
  projectSiteId?: string | null;
  typePayload?: Record<string, unknown>;
  attachmentFileIds?: string[];
}

export interface TenantTicketsQuery {
  page?: number;
  limit?: number;
  status?: SupportTicketStatus;
  queryType?: SupportQueryType;
  priority?: SupportPriority;
  search?: string;
}

export interface TenantTicketsResponse {
  tickets: SupportTicket[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export function useTenantTickets(params?: TenantTicketsQuery, options?: { enabled?: boolean }) {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.set('page', String(params.page));
  if (params?.limit) queryParams.set('limit', String(params.limit));
  if (params?.status) queryParams.set('status', params.status);
  if (params?.queryType) queryParams.set('queryType', params.queryType);
  if (params?.priority) queryParams.set('priority', params.priority);
  if (params?.search) queryParams.set('search', params.search);

  const qs = queryParams.toString();
  const url = `/api/v1/support/tickets${qs ? `?${qs}` : ''}`;

  return useQuery<TenantTicketsResponse, ApiError>({
    queryKey: ['tenantTickets', params],
    queryFn: async () => {
      return api.get<TenantTicketsResponse>(url);
    },
    enabled: options?.enabled !== undefined ? options.enabled : true,
  });
}

export function useTenantTicketDetail(ticketId: string | null | undefined) {
  return useQuery<TenantTicketDetail, ApiError>({
    queryKey: ['tenantTicket', ticketId],
    queryFn: async () => {
      if (!ticketId) throw new Error('Ticket ID is required');
      return api.get<TenantTicketDetail>(`/api/v1/support/tickets/${ticketId}`);
    },
    enabled: Boolean(ticketId),
  });
}

export function useCreateTicket() {
  const queryClient = useQueryClient();

  return useMutation<SupportTicket, ApiError, CreateTicketInput>({
    mutationFn: async (input: CreateTicketInput) => {
      return api.post<SupportTicket>('/api/v1/support/tickets', input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenantTickets'] });
    },
  });
}

export function useAddTenantMessage() {
  const queryClient = useQueryClient();

  return useMutation<SupportMessage, ApiError, { ticketId: string; body: string; attachmentFileIds?: string[] }>({
    mutationFn: async ({ ticketId, body, attachmentFileIds }) => {
      return api.post<SupportMessage>(`/api/v1/support/tickets/${ticketId}/messages`, {
        body,
        attachmentFileIds: attachmentFileIds || [],
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenantTicket', variables.ticketId] });
      queryClient.invalidateQueries({ queryKey: ['tenantTickets'] });
    },
  });
}

/**
 * Helper to upload attachment using presigned S3 URL
 */
export async function uploadTenantSupportAttachment(ticketId: string, file: File): Promise<{ fileId: string; name: string; size: number }> {
  const contentType = file.type || 'application/octet-stream';
  const { fileId, uploadUrl } = await api.post<{ fileId: string; uploadUrl: string }>(
    `/api/v1/support/tickets/${ticketId}/attachments/upload-url`,
    {
      filename: file.name,
      contentType,
      fileSize: file.size,
    }
  );

  const s3Response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': contentType,
    },
  });

  if (!s3Response.ok) {
    throw new Error(`Failed to upload file to storage: ${s3Response.statusText}`);
  }

  return { fileId, name: file.name, size: file.size };
}

/**
 * Helper to get presigned download URL and trigger file download
 */
export async function downloadTenantSupportAttachment(ticketId: string, attachmentId: string): Promise<void> {
  const { downloadUrl, filename } = await api.get<{ downloadUrl: string; filename: string }>(
    `/api/v1/support/tickets/${ticketId}/attachments/${attachmentId}/download`
  );

  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename || 'attachment';
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Public Support Ticket Hooks & Helpers (Phase 5A)
 */

export function useCreatePublicTicket() {
  return useMutation<CreatePublicTicketResponse, ApiError, CreatePublicTicketInput>({
    mutationFn: async (input: CreatePublicTicketInput) => {
      return api.post<CreatePublicTicketResponse>('/api/v1/public/support/tickets', input, {
        headers: {
          'x-skip-auth-redirect': 'true',
        },
      });
    },
  });
}

export function usePublicTicketDetail(ticketCode: string | null | undefined, secret: string | null | undefined) {
  return useQuery<PublicTicketDetail, ApiError>({
    queryKey: ['publicTicket', ticketCode, secret],
    queryFn: async () => {
      if (!ticketCode || !secret) throw new Error('Ticket code and secret are required');
      return api.get<PublicTicketDetail>(
        `/api/v1/public/support/tickets/${encodeURIComponent(ticketCode)}`,
        {
          headers: {
            'x-support-token': secret,
            'x-skip-auth-redirect': 'true',
          },
        }
      );
    },
    enabled: Boolean(ticketCode && secret),
    retry: 1,
  });
}

export function useAddPublicMessage() {
  const queryClient = useQueryClient();

  return useMutation<
    SupportMessage,
    ApiError,
    { ticketCode: string; secret: string; body: string; attachmentFileIds?: string[] }
  >({
    mutationFn: async ({ ticketCode, secret, body, attachmentFileIds }) => {
      return api.post<SupportMessage>(
        `/api/v1/public/support/tickets/${encodeURIComponent(ticketCode)}/messages`,
        {
          message: body,
          body,
          attachmentFileIds: attachmentFileIds || [],
        },
        {
          headers: {
            'x-support-token': secret,
            'x-skip-auth-redirect': 'true',
          },
        }
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['publicTicket', variables.ticketCode, variables.secret] });
    },
  });
}

export async function uploadPublicSupportAttachment(
  ticketCode: string,
  secret: string,
  file: File
): Promise<{ fileId: string; name: string; size: number }> {
  const contentType = file.type || 'application/octet-stream';
  const { fileId, uploadUrl } = await api.post<{ fileId: string; uploadUrl: string }>(
    `/api/v1/public/support/tickets/${encodeURIComponent(ticketCode)}/attachments/upload-url`,
    {
      filename: file.name,
      contentType,
      fileSize: file.size,
    },
    {
      headers: {
        'x-support-token': secret,
        'x-skip-auth-redirect': 'true',
      },
    }
  );

  const s3Response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': contentType,
    },
  });

  if (!s3Response.ok) {
    throw new Error(`Failed to upload file to storage: ${s3Response.statusText}`);
  }

  return { fileId, name: file.name, size: file.size };
}

export async function downloadPublicSupportAttachment(
  ticketCode: string,
  secret: string,
  attachmentId: string
): Promise<void> {
  const { downloadUrl, filename } = await api.get<{ downloadUrl: string; filename: string }>(
    `/api/v1/public/support/tickets/${encodeURIComponent(ticketCode)}/attachments/${encodeURIComponent(attachmentId)}/download`,
    {
      headers: {
        'x-support-token': secret,
        'x-skip-auth-redirect': 'true',
      },
    }
  );

  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = filename || 'attachment';
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}


